/**
 * FRONT-3146: the window-management rules, pure so every one has a sweep test
 * (`window-rules.test.ts`). `window-store.ts` is only a holder for this model.
 *
 * What a "window" is: an Eddy chat (or document) window, a Build session
 * window, the Pro Builder window, or a Settings window (User or System) — the full-attention
 * surfaces that used to open as exclusive full-screen overlays. They share one
 * model:
 *
 *  - at most `capacity` are visible at once, side by side: the person's
 *    "windows side by side" setting (1–5, Settings → Reading), lowered further
 *    while the content area is too narrow for that many (`capacityForWidth`);
 *  - the rest are minimized into the window bar (still mounted, so a running
 *    chat keeps streaming);
 *  - opening or restoring a window while two are visible does NOT silently
 *    pick a victim: the model records a pending placement and the UI asks the
 *    person which visible window to minimize;
 *  - opening something that is already open focuses it, never duplicates it.
 */

export type WindowKind = 'chat' | 'build' | 'pro-builder' | 'settings' | 'system-settings' | 'file' | 'app';

/**
 * `pending` is a NEW window that has not been placed yet because two others
 * are visible. It is in the list (so the chooser can name it) but is not
 * mounted or shown. A restore that hits the limit leaves its window
 * `minimized` and only sets `pendingId`.
 */
export type WindowState = 'visible' | 'minimized' | 'pending';

export interface WindowEntry {
  /** Stable key. Opening the same id again focuses the existing window. */
  id: string;
  kind: WindowKind;
  /** What the window shows (a session id, ...) — informational; dedupe is by id. */
  ref: string | null;
  title: string;
  state: WindowState;
}

export interface WindowsModel {
  /**
   * THE order: the top bar's tabs and the columns below both follow it (eddy,
   * 2026-10-27: "the order of windows below follows the tab order"). A new
   * window is appended on the right; only the person reorders it, by dragging
   * a tab (`moveWindow`) — no rule here ever moves an entry.
   */
  windows: WindowEntry[];
  /** The visible window that last got attention; Escape minimizes only this one. */
  focusedId: string | null;
  /** A window waiting for the person to choose which visible window gives way. */
  pendingId: string | null;
  /**
   * How many windows are shown side by side right now (1–`MAX_SIDE_BY_SIDE`).
   * Absent means `DEFAULT_CAPACITY`. Set only through `setCapacity`.
   */
  capacity?: number;
  /**
   * Windows minimized because the area got too narrow, not by the person.
   * They come back by themselves when it is wide enough again.
   */
  squeezedIds?: string[];
}

/** The most windows the person can ask to see side by side. */
export const MAX_SIDE_BY_SIDE = 5;
/** The model's capacity before the layer has measured and read the setting. */
export const DEFAULT_CAPACITY = 2;

/**
 * The narrowest a window reads comfortably; N windows need N times this
 * (eddy, 2026-10-26: "when I resize smaller, two windows are too dense").
 */
export const MIN_WINDOW_WIDTH = 560;
/** Extra width before another column comes back, so a drag at the edge does not flicker. */
const CAPACITY_HYSTERESIS = 40;

/**
 * How many windows to show side by side: the person's `preferred` count, but
 * never more than fit at `width` px. Dropping a column happens as soon as they
 * no longer fit; adding one back needs a little more, so a resize hovering at
 * a threshold never flips back and forth.
 */
export function capacityForWidth(width: number, preferred: number, current: number = preferred): number {
  const want = clampCapacity(preferred);
  const fitNow = Math.max(1, Math.floor(width / MIN_WINDOW_WIDTH));
  const fitWithMargin = Math.max(1, Math.floor((width - CAPACITY_HYSTERESIS) / MIN_WINDOW_WIDTH));
  const shown = Math.min(clampCapacity(current), want);
  if (fitNow < shown) return fitNow;
  return Math.min(want, Math.max(shown, fitWithMargin));
}

function clampCapacity(n: number): number {
  return Math.max(1, Math.min(MAX_SIDE_BY_SIDE, Math.floor(n) || 1));
}

export function capacityOf(model: WindowsModel): number {
  return model.capacity ?? DEFAULT_CAPACITY;
}

export const EMPTY_MODEL: WindowsModel = { windows: [], focusedId: null, pendingId: null };

export type OpenWindowInput = Pick<WindowEntry, 'id' | 'kind'> & Partial<Pick<WindowEntry, 'ref' | 'title'>>;

export function visibleWindows(model: WindowsModel): WindowEntry[] {
  return model.windows.filter((w) => w.state === 'visible');
}

export function findWindow(model: WindowsModel, id: string): WindowEntry | undefined {
  return model.windows.find((w) => w.id === id);
}

export function isVisible(model: WindowsModel, id: string): boolean {
  return findWindow(model, id)?.state === 'visible';
}

/** True while the person is being asked which visible window to minimize. */
export function needsChoice(model: WindowsModel): boolean {
  return model.pendingId !== null && findWindow(model, model.pendingId) !== undefined;
}

function patch(model: WindowsModel, id: string, change: Partial<WindowEntry>): WindowsModel {
  return { ...model, windows: model.windows.map((w) => (w.id === id ? { ...w, ...change } : w)) };
}

function focusAfterRemoval(model: WindowsModel, removedId: string): string | null {
  if (model.focusedId !== removedId) return model.focusedId;
  const visible = visibleWindows(model).filter((w) => w.id !== removedId);
  return visible.length > 0 ? visible[visible.length - 1].id : null;
}

/**
 * Places a pending NEW window as soon as there is room for it (a visible
 * window was minimized or closed some other way), so nobody is asked a
 * question whose answer is now obvious.
 */
function settlePending(model: WindowsModel): WindowsModel {
  if (model.pendingId === null) return model;
  const pending = findWindow(model, model.pendingId);
  if (!pending) return { ...model, pendingId: null };
  if (visibleWindows(model).length >= capacityOf(model)) return model;
  return { ...patch(model, pending.id, { state: 'visible' }), focusedId: pending.id, pendingId: null };
}

/**
 * Open (or focus) a window.
 *  - already open and visible: focus it;
 *  - already open and minimized: restore it (same limit as `restoreWindow`);
 *  - new, room available: visible and focused;
 *  - new, two already visible: added as `pending` and the model asks for a
 *    choice. A second open while one is pending replaces the first pending
 *    window — only one question is ever on screen.
 */
export function openWindow(model: WindowsModel, input: OpenWindowInput): WindowsModel {
  const existing = findWindow(model, input.id);
  if (existing) {
    const retitled = input.title && input.title !== existing.title ? patch(model, existing.id, { title: input.title }) : model;
    if (existing.state === 'visible') return { ...retitled, focusedId: existing.id };
    if (existing.state === 'minimized') return restoreWindow(retitled, existing.id);
    return retitled; // already waiting for a choice
  }
  const entry: WindowEntry = {
    id: input.id,
    kind: input.kind,
    ref: input.ref ?? null,
    title: input.title ?? '',
    state: 'visible',
  };
  if (visibleWindows(model).length < capacityOf(model)) {
    return { ...model, windows: [...model.windows, entry], focusedId: entry.id };
  }
  // One column: there is nothing to choose between — the new window takes it.
  if (capacityOf(model) === 1) {
    const base = squeezeVisible(dropPending(model));
    return { ...base, windows: [...base.windows, entry], focusedId: entry.id };
  }
  const withoutOldPending = dropPending(model);
  return {
    ...withoutOldPending,
    windows: [...withoutOldPending.windows, { ...entry, state: 'pending' }],
    pendingId: entry.id,
  };
}

/**
 * Open a window DOCKED BESIDE another one (FRONT-3146: Settings opens to the
 * right of the Builder / chat session the person is in, never as a modal on top
 * of it). The window `besideId` (or, when it is not visible, the focused or
 * last visible window) stays visible next to it; every
 * OTHER visible window is minimized — not closed, so its work survives and its
 * tab stays in the bar. Opening something already visible and docked just
 * focuses it. With no visible window there is nothing to sit beside, so it is a
 * plain `openWindow`. A waiting chooser question is cancelled: the placement
 * this rule just made answers it. Like every window, a new one is appended on
 * the right; one already open keeps its tab's place, so its column does too.
 */
export function openBeside(model: WindowsModel, input: OpenWindowInput, besideId?: string | null): WindowsModel {
  const visible = visibleWindows(model).filter((w) => w.id !== input.id);
  const anchor =
    visible.find((w) => w.id === besideId) ??
    visible.find((w) => w.id === model.focusedId) ??
    visible[visible.length - 1];
  if (!anchor) return openWindow(model, input);
  const existing = findWindow(model, input.id);
  // Already shown: opening again only focuses it, columns stay as they are.
  if (existing?.state === 'visible') return { ...model, focusedId: existing.id };
  const base = dropPending(model);
  // One column: the anchor gives way too (squeezed, so it returns beside the
  // new window when the area widens).
  const narrowed = capacityOf(base) >= 2 ? base : squeezeVisible(base);
  const minimizeOthers = (w: WindowEntry): WindowEntry =>
    w.state === 'visible' && w.id !== anchor.id ? { ...w, state: 'minimized' } : w;
  const windows = existing
    ? narrowed.windows.map((w) => (w.id === input.id ? { ...w, title: input.title || w.title, state: 'visible' as const } : minimizeOthers(w)))
    : [
        ...narrowed.windows.map(minimizeOthers),
        { id: input.id, kind: input.kind, ref: input.ref ?? null, title: input.title ?? '', state: 'visible' as const },
      ];
  return { ...narrowed, windows, focusedId: input.id };
}

/**
 * The person dragged a tab: `id` moves to sit just `side` of `targetId`, and
 * because the columns follow this same order, a shown window's column moves
 * with it. Nothing else changes — state, focus and any waiting question stay.
 */
export function moveWindow(model: WindowsModel, id: string, targetId: string, side: 'before' | 'after'): WindowsModel {
  const moving = findWindow(model, id);
  if (!moving || id === targetId || !findWindow(model, targetId)) return model;
  const without = model.windows.filter((w) => w.id !== id);
  const at = without.findIndex((w) => w.id === targetId) + (side === 'after' ? 1 : 0);
  const windows = [...without.slice(0, at), moving, ...without.slice(at)];
  if (windows.every((w, i) => w === model.windows[i])) return model;
  return { ...model, windows };
}

/** Removes a still-unplaced pending window (a restore-pending only clears the flag). */
function dropPending(model: WindowsModel): WindowsModel {
  if (model.pendingId === null) return model;
  const pending = findWindow(model, model.pendingId);
  if (pending?.state === 'pending') {
    return { ...model, windows: model.windows.filter((w) => w.id !== pending.id), pendingId: null };
  }
  return { ...model, pendingId: null };
}

/**
 * In one-column mode the window shown makes way for another: it is minimized
 * and remembered as squeezed, so it comes back beside it once there is room.
 */
function squeezeVisible(model: WindowsModel, keepId: string | null = null): WindowsModel {
  const out = visibleWindows(model).filter((w) => w.id !== keepId).map((w) => w.id);
  if (out.length === 0) return model;
  return {
    ...model,
    windows: model.windows.map((w) => (out.includes(w.id) ? { ...w, state: 'minimized' as const } : w)),
    squeezedIds: [...(model.squeezedIds ?? []).filter((id) => !out.includes(id)), ...out],
  };
}

/**
 * The area was resized or the person changed their side-by-side setting.
 * Fewer columns: the focused window and the next most recent stay, the rest
 * are minimized (remembered as squeezed), and with one column a waiting "which
 * one to replace?" question is answered by replacing what is left. More
 * columns: squeezed windows that are still minimized come back, newest first.
 * Anything the person did meanwhile wins — a squeezed window they closed,
 * minimized or restored themselves is no longer squeezed.
 */
export function setCapacity(model: WindowsModel, capacity: number): WindowsModel {
  const next = clampCapacity(capacity);
  if (next === capacityOf(model)) return model;
  let m: WindowsModel = { ...model, capacity: next };
  const visible = visibleWindows(m);
  if (visible.length > next) {
    const focused = visible.find((w) => w.id === m.focusedId) ?? visible[visible.length - 1]!;
    const others = visible.filter((w) => w.id !== focused.id);
    const keep = new Set([focused.id, ...others.slice(others.length - (next - 1)).map((w) => w.id)]);
    const out = visible.filter((w) => !keep.has(w.id)).map((w) => w.id);
    m = {
      ...m,
      windows: m.windows.map((w) => (out.includes(w.id) ? { ...w, state: 'minimized' as const } : w)),
      focusedId: focused.id,
      squeezedIds: [...(m.squeezedIds ?? []).filter((id) => !out.includes(id)), ...out],
    };
  }
  if (next === 1 && needsChoice(m)) {
    // One column: "which one should give way?" has only one answer.
    m = chooseReplace(m, visibleWindows(m)[0]!.id);
  }
  if (m.squeezedIds?.length) {
    const back: string[] = [];
    for (const id of [...m.squeezedIds].reverse()) {
      if (visibleWindows(m).length >= next) break;
      if (findWindow(m, id)?.state === 'minimized') {
        m = patch(m, id, { state: 'visible' });
        back.push(id);
      }
    }
    m = { ...m, squeezedIds: m.squeezedIds!.filter((id) => !back.includes(id) && findWindow(m, id)?.state === 'minimized') };
  }
  return settlePending(m);
}

/** Minimize a visible window. Never closes it, never touches the others. */
export function minimizeWindow(model: WindowsModel, id: string): WindowsModel {
  const w = findWindow(model, id);
  if (!w || w.state !== 'visible') return model;
  const next = { ...patch(model, id, { state: 'minimized' }), squeezedIds: model.squeezedIds?.filter((s) => s !== id) };
  return settlePending({ ...next, focusedId: focusAfterRemoval(next, id) });
}

/**
 * eddy, 2026-10-26: "when I click any of those [sidebar pages], minimize all
 * open windows" — going to a page means looking at the page. Every shown
 * window, and one still waiting for a column, goes to the window bar (still
 * mounted, a running chat keeps streaming). Nothing squeezed comes back by
 * itself afterwards: the person put them all away on purpose.
 */
export function minimizeAllWindows(model: WindowsModel): WindowsModel {
  if (!model.windows.some((w) => w.state !== 'minimized') && !model.squeezedIds?.length) return model;
  return {
    ...model,
    windows: model.windows.map((w) => (w.state === 'minimized' ? w : { ...w, state: 'minimized' as const })),
    focusedId: null,
    pendingId: null,
    squeezedIds: [],
  };
}

/**
 * Bring a minimized window back. With room it just becomes visible; with two
 * already visible it stays minimized and the model asks which one gives way.
 */
export function restoreWindow(model: WindowsModel, id: string): WindowsModel {
  const w = findWindow(model, id);
  if (!w) return model;
  if (w.state === 'visible') return { ...model, focusedId: id };
  if (w.state === 'pending') return model;
  if (visibleWindows(model).length < capacityOf(model)) {
    return { ...patch(dropPending(model), id, { state: 'visible' }), focusedId: id };
  }
  if (capacityOf(model) === 1) {
    return { ...patch(squeezeVisible(dropPending(model)), id, { state: 'visible' }), focusedId: id };
  }
  return { ...dropPending(model), pendingId: id };
}

/** The window bar's click: a visible window minimizes, a minimized one restores. */
export function toggleWindow(model: WindowsModel, id: string): WindowsModel {
  const w = findWindow(model, id);
  if (!w) return model;
  return w.state === 'visible' ? minimizeWindow(model, id) : restoreWindow(model, id);
}

/**
 * The person's answer to "which window do you want to minimize?": the chosen
 * visible window is minimized and the pending one takes its place.
 */
export function chooseReplace(model: WindowsModel, replaceId: string): WindowsModel {
  if (model.pendingId === null) return model;
  const pending = findWindow(model, model.pendingId);
  const victim = findWindow(model, replaceId);
  if (!pending || !victim || victim.state !== 'visible' || victim.id === pending.id) return model;
  let next = patch(model, victim.id, { state: 'minimized' });
  next = patch(next, pending.id, { state: 'visible' });
  // No reordering: columns follow the tabs (eddy, 2026-10-27), so the new
  // window shows where its tab is — on the right, where new tabs go.
  return { ...next, focusedId: pending.id, pendingId: null };
}

/**
 * eddy, 2026-10-26: "when two windows are open and I click New chat / Build
 * again while it asks me to pick one, start it in the background". The waiting
 * window is placed MINIMIZED — it mounts (a new build starts, a chat keeps its
 * draft) without replacing either visible window — and the pick ends.
 */
export function backgroundPending(model: WindowsModel): WindowsModel {
  if (model.pendingId === null || !findWindow(model, model.pendingId)) return model;
  return { ...patch(model, model.pendingId, { state: 'minimized' }), pendingId: null };
}

/**
 * Cancel the chooser. A NEW window that never got placed is not opened at all
 * (removed — its owner is told, see the store); a window being restored simply
 * stays minimized. The two visible windows are untouched either way.
 */
export function cancelPending(model: WindowsModel): WindowsModel {
  return dropPending(model);
}

/** Close a window for good. Frees a slot, which places a waiting pending window. */
export function closeWindow(model: WindowsModel, id: string): WindowsModel {
  if (!findWindow(model, id)) return model;
  const remaining: WindowsModel = {
    ...model,
    windows: model.windows.filter((w) => w.id !== id),
    pendingId: model.pendingId === id ? null : model.pendingId,
  };
  return settlePending({ ...remaining, focusedId: focusAfterRemoval(remaining, id) });
}

export function setWindowTitle(model: WindowsModel, id: string, title: string): WindowsModel {
  const w = findWindow(model, id);
  if (!w || w.title === title) return model;
  return patch(model, id, { title });
}

/** Moves a window onto another thing without changing its slot (the "+" in a chat window). */
export function retargetWindow(model: WindowsModel, id: string, next: { id: string; ref: string | null; title: string }): WindowsModel {
  const w = findWindow(model, id);
  if (!w) return model;
  if (next.id !== id && findWindow(model, next.id)) return model; // never create a duplicate
  const windows = model.windows.map((x) => (x.id === id ? { ...x, id: next.id, ref: next.ref, title: next.title } : x));
  return {
    ...model,
    windows,
    squeezedIds: model.squeezedIds?.map((s) => (s === id ? next.id : s)),
    focusedId: model.focusedId === id ? next.id : model.focusedId,
    pendingId: model.pendingId === id ? next.id : model.pendingId,
  };
}

export function focusWindow(model: WindowsModel, id: string): WindowsModel {
  if (!isVisible(model, id) || model.focusedId === id) return model;
  return { ...model, focusedId: id };
}

/**
 * Escape acts only on the window that has the person's attention — the column
 * under the mouse (set on pointer enter) — and since 2026-10-26 it CLOSES that
 * window (eddy: "the one where the mouse is over should be active, so ESC
 * closes it"). With two visible windows both listen for the key, so each asks
 * this first. The name predates that; it answers "is Escape mine?".
 */
export function shouldMinimizeOnEscape(model: WindowsModel, id: string, visibleCount = visibleWindows(model).length): boolean {
  // While the person is picking which column to replace, Escape cancels the
  // pick (window-chooser.tsx) — it must not also close a window.
  if (needsChoice(model)) return false;
  if (!isVisible(model, id)) return false;
  return visibleCount <= 1 || model.focusedId === id;
}

/**
 * Closing a window opened from another page used to navigate back there. Now
 * that windows persist across pages, only do it when this was the last window
 * and the person is still on the page the window opened over (home).
 */
export function shouldReturnToOrigin(args: {
  returnTo: string | null;
  currentPathIsHome: boolean;
  remainingWindows: number;
}): boolean {
  return args.returnTo !== null && args.currentPathIsHome && args.remainingWindows === 0;
}
